import { ref } from "@vue/composition-api";
import axios from "@/libs/axios";
import { getUserData } from "@/auth/getUserData";
import useUsers from "@/composables/useUsers";

const chats = ref([]);
const isFetchingChats = ref(false);
const usersMap = new Map();

export default function useTeamChat() {
    const createForm = ref(false);
    const editChat = ref(null);

    const leaveTeamChat = async (chatId, participantId) => {
        const chat = chats.value.find((chat) => chat.id === chatId);
        if (chat) {
            chat.isArchived = true;
        }

        const data = await axios.patch(`/team-chats/${chatId}/participants/${participantId}`).catch(() => {
            if (chat) {
                chat.isArchived = false;
            }
        });

        return data;
    };

    const deleteTeamChat = async (chatId) => {
        const chat = chats.value.find((chat) => chat.id === chatId);
        if (chat) {
            chat.isArchived = true;
        }

        const data = await axios.delete(`/team-chats/${chatId}`).catch(() => {
            if (chat) {
                chat.isArchived = false;
            }
        });

        return data;
    };

    const toggleCreateTeamChat = () => {
        createForm.value = !createForm.value;
        if (!createForm.value) {
            editChat.value = null;
        }
    };

    const setEditedChat = (id) => {
        editChat.value = id;
    };

    const fetchChats = async () => {
        isFetchingChats.value = true;
        const loggedUser = getUserData();
        const { users } = useUsers();

        // add logged user and already fetched users
        if (usersMap.size === 0) {
            usersMap.set(loggedUser.id, loggedUser);
            users.value.forEach((user) => usersMap.set(user.id, user));
        }

        const fetchedChats = (await axios.get("/team-chats")).data;

        fetchedChats.forEach(
            (chat) =>
                (chat.isArchived =
                    Boolean(chat.deletedAt) ||
                    Boolean(chat.participants.find((participant) => participant.userId === loggedUser.id)?.leftAt))
        );

        const usersIds = fetchedChats.map((chat) => chat.participants.map((participant) => participant.userId)).flat();

        // filter unique and not fetched users ids
        const uniqueUnfetchedUsersIds = usersIds.filter(
            (value, index, self) => self.indexOf(value) === index && !usersMap.has(value)
        );

        // fetch users
        let fetchedUsers = [];
        if (uniqueUnfetchedUsersIds.length > 0) {
            fetchedUsers = (
                await axios.get(`/users`, {
                    params: {
                        s: {
                            $or: uniqueUnfetchedUsersIds.map((uniqueUnfetchedUsersId) => ({
                                id: uniqueUnfetchedUsersId,
                            })),
                        },
                    },
                })
            ).data;
        }

        fetchedUsers.forEach((user) => usersMap.set(user.id, user));
        fetchedChats.forEach((chat) => {
            chat.usersTyping = [];
            chat.participants.forEach((participant) => (participant.user = usersMap.get(participant.userId)));
        });

        chats.value = fetchedChats;
        isFetchingChats.value = false;
        return fetchedChats;
    };

    const fetchChat = (chatId) => {
        return axios.get(`/team-chats/${chatId}`).then((response) => response.data);
    };

    const removeChat = (chatId) => {
        chats.value = chats.value.filter((chat) => chat.id !== chatId);
    };

    const setParticipantChatStatus = (chatId, participantId, chatStatus) => {
        const chat = chats.value.find((chat) => chat.id === chatId);

        if (chatStatus === "read") {
            chat.unreadMessages = 0;
            // TODO: change to updateEntryStatus function when ssx-48 is merged
            axios.patch(`/team-chats/${chatId}/participants/${participantId}/entries`, { status: "read" });
        }

        chat.participants.find((participant) => participant.id === participantId).chatStatus = chatStatus;

        return axios.patch(`/team-chats/${chatId}/participants/${participantId}`, { chatStatus });
    };

    const updateEntryStatus = (status, chatId, participantId, participantEntryId) => {
        if (!participantId) {
            axios.patch(`/team-chats/entries`, { status });
        } else if (!participantEntryId) {
            axios.patch(`/team-chats/${chatId}/participants/${participantId}/entries`, { status });
        } else {
            axios.patch(`/team-chats/${chatId}/participants/${participantId}/entries/${participantEntryId}`, {
                status,
            });
        }
    };

    return {
        createForm,
        editChat,
        chats,
        isFetchingChats,

        // Methods
        toggleCreateTeamChat,
        setEditedChat,
        fetchChats,
        fetchChat,
        removeChat,
        setParticipantChatStatus,
        updateEntryStatus,
        leaveTeamChat,
        deleteTeamChat,
    };
}
